import { Component } from '@theme/component';
import { requestIdleCallback, throttle } from '@theme/utilities';

/**
 * Product options scroller component for star-cross tile patterns.
 * Handles product variant selection, checkbox states, and quantity input management.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} wrapper
 * @property {HTMLElement} scrollbar
 * @property {HTMLElement} thumb
 * @property {HTMLElement} controls
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @property {HTMLInputElement} [showMainProductCheckbox]
 *
 * @extends {Component<Refs>}
 */
class ProductOptionsScrollerStarCross extends Component {
  requiredRefs = ['wrapper'];

  /**
   * Currently active button
   * @type {HTMLButtonElement | null}
   */
  #activeButton = null;

  /**
   * Whether this is the first or second scroller
   * @type {'first' | 'second'}
   */
  #scrollerType = 'first';

  /**
   * Form element reference
   * @type {HTMLFormElement | null}
   */
  #form = null;

  /**
   * Quantity calculator reference
   * @type {HTMLElement | null}
   */
  #quantityCalculator = null;

  /**
   * Media gallery reference
   * @type {HTMLElement | null}
   */
  #mediaGallery = null;

  /**
   * Slideshow reference
   * @type {HTMLElement | null}
   */
  #slideshow = null;

  /**
   * Current selected product IDs
   * @type {{first: string | null, second: string | null}}
   */
  #selectedProducts = { first: null, second: null };

  /** @type {(() => void) | null} */
  #abortDrag = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();
    
    // Hide second sample form on page load (do this once)
    if (!window.__secondSampleFormHidden) {
      const samplePhotosContainer = document.querySelector('.product-information__sample-photos');
      if (samplePhotosContainer) {
        const sampleFormBlocks = Array.from(samplePhotosContainer.querySelectorAll('.shopify-block')).filter(block => 
          block.querySelector('.product-sample__form')
        );
        
        // Hide the SECOND block (index 1)
        if (sampleFormBlocks[1]) {
          sampleFormBlocks[1].style.display = 'none';
          window.__secondSampleFormHidden = true;
        }
      }
    }
    
    requestIdleCallback(() => {
      this.#init();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Abort any ongoing drag operation
    if (this.#abortDrag) {
      this.#abortDrag();
      this.#abortDrag = null;
    }
    
    this.refs.wrapper?.removeEventListener('scroll', this.#handleScroll);
    this.#resizeObserver?.disconnect();
    
    if (this.refs.thumb) {
      this.refs.thumb.removeEventListener('mousedown', this.#handleThumbMouseDown);
    }
    
    if (this.refs.scrollbar) {
      this.refs.scrollbar.removeEventListener('click', this.#handleScrollbarClick);
    }
  }

  /**
   * Initialize the component
   */
  #init() {
    if (!this.refs.wrapper) return;

    const allScrollers = Array.from(document.querySelectorAll('product-options-scroller-star-cross'));
    const scrollerIndex = allScrollers.indexOf(this);
    this.#scrollerType = scrollerIndex === 0 ? 'first' : 'second';

    this.#form = document.querySelector('form[action*="/cart/add"]');
    this.#quantityCalculator = document.querySelector('quantity-calculator-star-cross');
    this.#mediaGallery = document.querySelector('.media-gallery__grid');
    this.#slideshow = document.querySelector('media-gallery slideshow-component');

    const labelText = this.querySelector('.product-meta-variants__label')?.textContent?.trim() || '';
    const label = labelText.split(' ')[0];
    if (this.#quantityCalculator && label) {
      const event = new CustomEvent('scroller-initialized', {
        detail: { scrollerType: this.#scrollerType, label },
        bubbles: true
      });
      this.#quantityCalculator.dispatchEvent(event);
    }

    this.#setupButtons();

    this.#setupCheckbox();

    this.#setupScroll();

    this.#setupCombinationListener();

    const activeBtn = this.refs.wrapper.querySelector('.active');
    if (activeBtn) {
      this.#activeButton = /** @type {HTMLButtonElement} */ (activeBtn);
      
      const variantId = /** @type {HTMLButtonElement} */ (activeBtn).dataset.variantId;
      const productId = /** @type {HTMLButtonElement} */ (activeBtn).dataset.productId;
      
      if (variantId) {
        this.#updateFormVariantId(variantId);
      }
      
      if (productId) {
        this.#selectedProducts[this.#scrollerType] = productId;
      }
      
      this.#updateSampleForm(activeBtn);
    }
  }

  /**
   * Setup product option buttons
   */
  #setupButtons() {
    const buttons = this.refs.wrapper.querySelectorAll('button');
    
    buttons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.#handleButtonClick(/** @type {HTMLButtonElement} */ (button));
      });
    });
  }

  /**
   * Handle button click
   * @param {HTMLButtonElement} button
   */
  #handleButtonClick(button) {
    const allButtons = this.refs.wrapper.querySelectorAll('button');
    allButtons.forEach(btn => btn.classList.remove('active'));

    button.classList.add('active');
    this.#activeButton = button;

    const productUrl = button.dataset.productUrl;
    const productId = button.dataset.productId;
    const variantId = button.dataset.variantId;
    const sku = button.dataset.sku;
    const sampleHandle = button.dataset.sampleHandle;
    const sampleImg = button.dataset.sampleImg;
    const sampleVariantId = button.dataset.sampleVariantId;

    this.#updateFormVariantId(variantId);

    this.#updateProductTitle(button.getAttribute('aria-label') || '');

    this.#selectedProducts[this.#scrollerType] = productId || null;

    this.#updateSampleForm(button);

    this.#updateCombinationImage();

    this.#dispatchProductChange({
      productUrl,
      productId,
      variantId,
      sku,
      sampleHandle,
      sampleImg,
      sampleVariantId,
      scrollerType: this.#scrollerType
    });
  }

  /**
   * Update form variant ID
   * @param {string | undefined} variantId
   */
  #updateFormVariantId(variantId) {
    if (!variantId) return;

    if (this.#quantityCalculator) {
      const inputRef = this.#scrollerType === 'first' 
        ? 'firstVariantIdInput' 
        : 'secondVariantIdInput';
      
      const input = /** @type {any} */ (this.#quantityCalculator)?.refs?.[inputRef];
      if (input) {
        input.value = variantId;
      }
    }
  }

  /**
   * Update product title in data attributes
   * @param {string} title
   */
  #updateProductTitle(title) {
    const selector = this.#scrollerType === 'first' 
      ? '[data-product-title-first]' 
      : '[data-product-title-second]';

    const titleElement = document.querySelector(selector);
    if (titleElement) {
      titleElement.textContent = title;
    }

    const scrollerTitle = this.querySelector('.product-meta-variants__product-title');
    if (scrollerTitle) {
      scrollerTitle.textContent = title;
    }
  }

  /**
   * Hide sample form for specific type
   * @param {'first' | 'second'} type
   */
  #hideSampleForm(type) {
    const samplePhotosContainer = document.querySelector('.product-information__sample-photos');
    if (!samplePhotosContainer) return;

    const sampleFormBlocks = Array.from(samplePhotosContainer.querySelectorAll('.shopify-block')).filter(block => 
      block.querySelector('.product-sample__form')
    );
    
    const index = type === 'first' ? 0 : 1;
    
    if (sampleFormBlocks[index]) {
      sampleFormBlocks[index].style.display = 'none';
    }
  }

  /**
   * Show sample form for specific type
   * @param {'first' | 'second'} type
   */
  #showSampleForm(type) {
    const samplePhotosContainer = document.querySelector('.product-information__sample-photos');
    if (!samplePhotosContainer) return;

    const sampleFormBlocks = Array.from(samplePhotosContainer.querySelectorAll('.shopify-block')).filter(block => 
      block.querySelector('.product-sample__form')
    );
    
    const index = type === 'first' ? 0 : 1;
    
    if (sampleFormBlocks[index]) {
      sampleFormBlocks[index].style.display = '';
    }
  }

  /**
   * Update sample form with product data
   * @param {HTMLButtonElement} button
   */
  #updateSampleForm(button) {
    const samplePhotosContainer = document.querySelector('.product-information__sample-photos');
    if (!samplePhotosContainer) return;

    const sampleFormBlocks = Array.from(samplePhotosContainer.querySelectorAll('.shopify-block')).filter(block => 
      block.querySelector('.product-sample__form')
    );
    
    const index = this.#scrollerType === 'first' ? 0 : 1;
    const sampleFormBlock = sampleFormBlocks[index];
    
    if (!sampleFormBlock) return;

    const sampleForm = sampleFormBlock.querySelector('product-form-component');
    if (!sampleForm) return;

    this.#showSampleForm(this.#scrollerType);

    const productTitle = button.getAttribute('aria-label') || '';
    const sampleVariantId = button.dataset.sampleVariantId;
    const sampleImg = button.dataset.sampleImg;
    const sampleHandle = button.dataset.sampleHandle;

    const variantInputs = sampleForm.querySelectorAll('input[name="id"]');
    variantInputs.forEach(input => {
      if (sampleVariantId) {
        input.value = sampleVariantId;
      }
    });

    const titleElement = sampleForm.querySelector('.product-sample__title');
    if (titleElement) {
      const link = titleElement.querySelector('a');
      if (link) {
        link.textContent = productTitle;
        if (sampleHandle) {
          link.href = `/products/${sampleHandle}`;
        }
      } else {
        titleElement.textContent = productTitle;
      }
    }

    const imageContainer = sampleForm.querySelector('.product-sample__image');
    if (imageContainer && sampleImg) {
      const img = imageContainer.querySelector('img');
      const link = imageContainer.querySelector('a');
      
      if (img && img.src) {
        const newFileName = sampleImg.split('/').pop();
        const currentSrc = img.src;
        const srcParts = currentSrc.split('/');
        srcParts[srcParts.length - 1] = newFileName;
        const newSrc = srcParts.join('/');
        
        img.src = newSrc;
        if (img.srcset) {
          img.srcset = newSrc;
        }
      }
      
      if (link && sampleHandle) {
        link.href = `/products/${sampleHandle}`;
      }
    }
  }

  /**
   * Setup checkbox functionality
   */
  #setupCheckbox() {
    const checkbox = /** @type {HTMLInputElement | null} */ (this.refs.showMainProductCheckbox);
    if (!checkbox) return;

    checkbox.addEventListener('change', () => {
      this.#handleCheckboxChange(checkbox.checked);
    });

    this.#handleCheckboxChange(checkbox.checked);
  }

  /**
   * Handle checkbox change
   * @param {boolean} checked
   */
  #handleCheckboxChange(checked) {
    if (!this.#quantityCalculator) return;

    const inputRefs = this.#scrollerType === 'first'
      ? ['firstSqFtInput', 'firstUnitsInput', 'firstSqFtWrapper', 'firstPerUnitInputs']
      : ['secondSqFtInput', 'secondUnitsInput', 'secondSqFtWrapper', 'secondPerUnitInputs'];

    inputRefs.forEach(refName => {
      const element = /** @type {any} */ (this.#quantityCalculator)?.refs?.[refName];
      if (!element) return;

      if (checked) {
        if (element.tagName === 'INPUT') {
          element.disabled = true;
          element.value = '';
          element.style.opacity = '0.5';
          element.style.pointerEvents = 'none';
        } else {
          element.style.opacity = '0.5';
          element.style.pointerEvents = 'none';
          const inputs = element.querySelectorAll('input');
          inputs.forEach(/** @param {HTMLInputElement} input */ (input) => {
            input.disabled = true;
            input.value = '';
          });
        }
      } else {
        if (element.tagName === 'INPUT') {
          element.disabled = false;
          element.style.opacity = '1';
          element.style.pointerEvents = 'auto';
        } else {
          element.style.opacity = '1';
          element.style.pointerEvents = 'auto';
          const inputs = element.querySelectorAll('input');
          inputs.forEach(/** @param {HTMLInputElement} input */ (input) => {
            input.disabled = false;
          });
        }
      }
    });

    if (checked && this.#quantityCalculator) {
      const quantityInputRef = this.#scrollerType === 'first' 
        ? 'firstQuantityInput' 
        : 'secondQuantityInput';
      
      const quantityInput = /** @type {any} */ (this.#quantityCalculator)?.refs?.[quantityInputRef];
      if (quantityInput) {
        quantityInput.value = '0';
      }
    }
  }

  /**
   * Dispatch product change event
   * @param {Object} data
   */
  #dispatchProductChange(data) {
    const event = new CustomEvent('product-option-changed', {
      detail: data,
      bubbles: true
    });
    this.dispatchEvent(event);
  }

  /**
   * Setup listener for combination changes from other scrollers
   */
  #setupCombinationListener() {
    document.addEventListener('product-option-changed', (/** @type {CustomEvent} */ event) => {
      const { productId, scrollerType } = event.detail;
      
      if (scrollerType && scrollerType !== this.#scrollerType && productId) {
        this.#selectedProducts[scrollerType] = productId;
        this.#updateCombinationImage();
      }
    });
  }

  /**
   * Update combination image in media gallery
   */
  #updateCombinationImage() {
    if (!this.#mediaGallery) return;
    if (!this.#selectedProducts.first || !this.#selectedProducts.second) return;

    const combinations = /** @type {any} */ (window).combinations;
    if (!combinations || !Array.isArray(combinations)) return;

    const firstId = parseInt(this.#selectedProducts.first);
    const secondId = parseInt(this.#selectedProducts.second);
    
    const matchingCombo = combinations.find(combo => {
      if (!combo.products || combo.products.length < 2) return false;
      return (
        (combo.products[0] === firstId && combo.products[1] === secondId) ||
        (combo.products[0] === secondId && combo.products[1] === firstId)
      );
    });

    if (!matchingCombo || !matchingCombo.image) return;

    const firstLi = this.#mediaGallery.querySelector('li');
    if (!firstLi) return;

    const productMedia = firstLi.querySelector('.product-media');
    if (!productMedia) return;

    const img = productMedia.querySelector('img.product-media__image');
    if (!img) return;

    this.#duplicateFirstImage(firstLi);

    const combinationImageUrl = matchingCombo.image;
    
    const urlParts = combinationImageUrl.split('?');
    const baseUrl = urlParts[0];
    
    const widths = [240, 352, 832, 1200, 1600, 1920, 2560, 3840];
    const srcsetParts = widths.map(width => {
      return `${baseUrl}?width=${width} ${width}w`;
    });
    
    img.src = `${baseUrl}?width=3840`;
    img.srcset = srcsetParts.join(', ');

    this.#updateSlideshowImage(baseUrl, srcsetParts);
  }

  /**
   * Duplicate the first image in the gallery
   * @param {HTMLElement} firstLi - The first li element to duplicate
   */
  #duplicateFirstImage(firstLi) {
    if (!this.#mediaGallery || !firstLi) return;

    if (firstLi.dataset.combinationDuplicate) return;

    const clone = /** @type {HTMLElement} */ (firstLi.cloneNode(true));
    
    const disclaimerButton = clone.querySelector('.product-media-disclaimer-button');
    if (disclaimerButton) {
      disclaimerButton.remove();
    }
    
    firstLi.dataset.combinationDuplicate = 'original';
    clone.dataset.combinationDuplicate = 'clone';

    firstLi.after(clone);
  }

  /**
   * Update slideshow image for mobile view
   * @param {string} baseUrl - The base URL of the combination image
   * @param {string[]} srcsetParts - The srcset parts for responsive images
   */
  #updateSlideshowImage(baseUrl, srcsetParts) {
    if (!this.#slideshow) return;

    const slideshowSlides = this.#slideshow.querySelector('slideshow-slides');
    if (!slideshowSlides) return;

    const existingCombinationSlide = slideshowSlides.querySelector('[data-combination-slide]');
    if (existingCombinationSlide) {
      const img = existingCombinationSlide.querySelector('img.product-media__image');
      if (img) {
        img.src = `${baseUrl}?width=3840`;
        img.srcset = srcsetParts.join(', ');
      }
      
      this.#scrollToFirstSlide();
      return;
    }

    const firstSlide = slideshowSlides.querySelector('slideshow-slide');
    if (!firstSlide) return;

    const newSlide = /** @type {HTMLElement} */ (firstSlide.cloneNode(true));
    
    newSlide.dataset.combinationSlide = 'true';
    
    const zoomButton = newSlide.querySelector('.product-media-container__zoom-button');
    if (zoomButton) {
      zoomButton.remove();
    }
    
    const disclaimerButton = newSlide.querySelector('.product-media-disclaimer-button');
    if (disclaimerButton) {
      disclaimerButton.remove();
    }

    const img = newSlide.querySelector('img.product-media__image');
    if (img) {
      img.src = `${baseUrl}?width=3840`;
      img.srcset = srcsetParts.join(', ');
      img.alt = 'Product Combination';
    }

    slideshowSlides.insertBefore(newSlide, firstSlide);
    
    this.#scrollToFirstSlide();
  }

  /**
   * Scroll slideshow to first slide
   */
  #scrollToFirstSlide() {
    if (!this.#slideshow) return;

    const slideshowComponent = /** @type {any} */ (this.#slideshow);
    if (typeof slideshowComponent.select === 'function') {
      setTimeout(() => {
        slideshowComponent.select(0, undefined, { animate: true });
      }, 100);
    }
  }

  /**
   * Setup scroll functionality with navigation buttons and scrollbar
   */
  #setupScroll() {
    const { wrapper, previous, next, scrollbar, thumb, controls } = this.refs;
    if (!wrapper) return;

    if (previous) {
      previous.addEventListener('click', (e) => {
        e.preventDefault();
        const scrollAmount = wrapper.clientWidth * 0.8;
        wrapper.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
    }

    if (next) {
      next.addEventListener('click', (e) => {
        e.preventDefault();
        const scrollAmount = wrapper.clientWidth * 0.8;
        wrapper.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
    }

    if (scrollbar && thumb) {
      thumb.addEventListener('mousedown', this.#handleThumbMouseDown);
      scrollbar.addEventListener('click', this.#handleScrollbarClick);
    }

    wrapper.addEventListener('scroll', this.#handleScroll);

    this.#resizeObserver = new ResizeObserver(() => {
      this.#updateControls();
      this.#updateScrollbar();
    });
    this.#resizeObserver.observe(wrapper);

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#updateControls();
        this.#updateScrollbar();
      });
    });
  }

  /**
   * Update visibility and state of navigation controls
   */
  #updateControls = () => {
    const { wrapper, previous, next, controls, scrollbar } = this.refs;
    
    if (!wrapper) return;

    const needsScroll = wrapper.scrollWidth > wrapper.clientWidth;
    const atStart = wrapper.scrollLeft <= 1;
    const atEnd = wrapper.scrollLeft + wrapper.clientWidth >= wrapper.scrollWidth - 1;

    if (controls) {
      controls.classList.toggle('is-visible', needsScroll);
    }
    
    if (previous) {
      previous.disabled = atStart;
    }
    
    if (next) {
      next.disabled = atEnd;
    }

    if (scrollbar) {
      scrollbar.classList.toggle('is-visible', needsScroll);
    }
  };

  /**
   * Update scrollbar thumb position and size
   */
  #updateScrollbar = () => {
    const { wrapper, thumb, scrollbar } = this.refs;
    
    if (!wrapper || !thumb || !scrollbar) return;

    const scrollRatio = wrapper.clientWidth / wrapper.scrollWidth;
    const minThumbWidth = Math.max(scrollbar.clientWidth * 0.1, 20);
    const thumbWidth = Math.max(scrollbar.clientWidth * scrollRatio, minThumbWidth);
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
    const scrollProgress = maxScroll > 0 ? wrapper.scrollLeft / maxScroll : 0;
    const maxThumbPosition = scrollbar.clientWidth - thumbWidth;
    const thumbPosition = maxThumbPosition * scrollProgress;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${thumbPosition}px)`;
  };

  /**
   * Handle scroll events (throttled)
   */
  #handleScroll = throttle(() => {
    this.#updateControls();
    this.#updateScrollbar();
  }, 16);

  /**
   * Handle thumb mousedown for dragging
   * @param {MouseEvent} event
   */
  #handleThumbMouseDown = (event) => {
    event.preventDefault();
    
    const { wrapper, thumb, scrollbar } = this.refs;
    if (!wrapper || !thumb || !scrollbar) return;

    const startX = event.clientX;
    const startScrollLeft = wrapper.scrollLeft;
    const scrollbarRect = scrollbar.getBoundingClientRect();
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
    
    /**
     * @param {MouseEvent} e
     */
    const onMouseMove = (e) => {
      if (!this.isConnected) {
        cleanup();
        return;
      }
      
      const deltaX = e.clientX - startX;
      const scrollRatio = maxScroll / (scrollbarRect.width - thumb.offsetWidth);
      const newScrollLeft = startScrollLeft + (deltaX * scrollRatio);
      
      wrapper.scrollLeft = Math.max(0, Math.min(maxScroll, newScrollLeft));
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      this.#abortDrag = null;
    };

    const onMouseUp = () => {
      cleanup();
    };

    this.#abortDrag = cleanup;

    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'grabbing';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  /**
   * Handle scrollbar click to jump to position
   * @param {MouseEvent} event
   */
  #handleScrollbarClick = (event) => {
    if (event.target === this.refs.thumb) return;
    
    const { wrapper, scrollbar, thumb } = this.refs;
    if (!wrapper || !scrollbar || !thumb) return;

    const scrollbarRect = scrollbar.getBoundingClientRect();
    const clickPosition = event.clientX - scrollbarRect.left;
    const scrollRatio = clickPosition / scrollbarRect.width;
    const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
    
    wrapper.scrollTo({
      left: maxScroll * scrollRatio,
      behavior: 'smooth'
    });
  };
}

if (!customElements.get('product-options-scroller-star-cross')) {
  customElements.define('product-options-scroller-star-cross', ProductOptionsScrollerStarCross);
}
